import {
  MedicationApiDto,
  toMedication,
  toPrescribedDiabetesMedication,
  type Medication,
  type PrescribedDiabetesMedication,
  type PrescribedDiabetesMedicationApiDto,
} from "@/model/Medication"
import {
  PatientAppStateApiDto,
  toPatientAppState,
  type PatientAppState,
} from "@/model/PatientAppState"
import {
  ShippingAddressApiDto,
  toShippingAddress,
  type ShippingAddress,
} from "@/model/ShippingAddress"
import { LocalDate } from "@js-joda/core"
import {
  DeviceDistributionMethod,
  DeviceDistributionMethodApiDto,
} from "./DeviceDistributionMethod"

export const patientStatuses = ["onboarding", "monitoring", "offboarded"] as const
export type PatientStatusApiDto = (typeof patientStatuses)[number]
export type PatientStatus = PatientStatusApiDto

export type Self = {
  id: string
  firstName: string
  lastName: string
  address: ShippingAddress | null
  status: PatientStatus
  numberOfFetuses: number | null
  weightInKilograms: number | null
  heightInCentimeters: number | null
  estimatedDateOfDelivery: LocalDate | null
  medications: Medication[]
  prescribedDiabetesMedications: PrescribedDiabetesMedication[]
  hasBloodGlucoseMonitor: boolean
  shouldScanBloodGlucoseDevice: boolean
  deviceDistributionMethod: DeviceDistributionMethod
  patientAppState: PatientAppState
  lastLiveInteractionDate?: LocalDate | null
}

export type SelfApiDto = {
  id: string
  firstName: string
  lastName: string
  address: ShippingAddressApiDto | null
  status: PatientStatusApiDto
  numberOfFetuses: number | null
  weightInKilograms: number | null
  heightInCentimeters: number | null
  estimatedDateOfDelivery: string | null
  medications: MedicationApiDto[]
  prescribedDiabetesMedications: PrescribedDiabetesMedicationApiDto[]
  hasBloodGlucoseMonitor: boolean
  shouldScanBloodGlucoseDevice: boolean
  deviceDistributionMethod: DeviceDistributionMethodApiDto
  patientAppState: PatientAppStateApiDto
  lastLiveInteractionDate?: string | null
}

export const toSelf = (dto: SelfApiDto): Self => ({
  id: dto.id,
  firstName: dto.firstName,
  lastName: dto.lastName,
  address: dto.address ? toShippingAddress(dto.address) : null,
  status: dto.status,
  numberOfFetuses: dto.numberOfFetuses,
  weightInKilograms: dto.weightInKilograms,
  heightInCentimeters: dto.heightInCentimeters,
  estimatedDateOfDelivery: dto.estimatedDateOfDelivery
    ? LocalDate.parse(dto.estimatedDateOfDelivery)
    : null,
  medications: dto.medications.map(toMedication),
  prescribedDiabetesMedications: (dto.prescribedDiabetesMedications ?? []).map(
    toPrescribedDiabetesMedication,
  ),
  hasBloodGlucoseMonitor: dto.hasBloodGlucoseMonitor,
  shouldScanBloodGlucoseDevice: dto.shouldScanBloodGlucoseDevice,
  deviceDistributionMethod: dto.deviceDistributionMethod,
  patientAppState: toPatientAppState(dto.patientAppState),
  lastLiveInteractionDate: dto.lastLiveInteractionDate
    ? LocalDate.parse(dto.lastLiveInteractionDate)
    : null,
})
